/**
 * MapCompactPreview Component
 * The collapsed 317x317 tile, baked as the locked Grid bleed/Pocket globe/
 * Feather treatment: a level grid field feathered out on all four sides (no
 * line meets the tile rim), the pocket meridian orb tucked at the lower-right
 * rim, and the numbered place chips over the composition.
 *
 * It MUST NOT mount `MapCanvas`. `Cmarkdownvisual` renders the compact tile and
 * the portalled children at the same time, so a canvas here would mean a live
 * WebGL context for every collapsed map on the page — and browsers cap those at
 * roughly 16. This restraint is the whole defence against context exhaustion.
 */

'use client';

import type { CSSProperties, ReactElement } from 'react';
import { getFontFamily } from '@/theme/typography';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import {
  isCoordinateDetailRow,
  mapLocationRowText,
  type MapLocationEntry,
} from './map-render-model';
import './MapCompactPreview.css';

/** Chips shown on a 317 tile before the rest are implied by overflow. */
const COMPACT_PLACE_LIMIT = 4;

/** Bidi direction for the place chips, which carry arbitrary-script names. */
const AUTO_TEXT_DIRECTION = 'auto';

const COMPACT_PREVIEW_ROOT_STYLE: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minWidth: 0,
  overflow: 'hidden',
};

const PLACE_LABEL_STYLE: CSSProperties = {
  fontFamily: getFontFamily(),
};

/** Deterministic chip seats so the same places land in the same spots. */
const GRID_SEATS: ReadonlyArray<{ left: string; top: string }> = [
  { left: '-4%', top: '16%' },
  { left: '48%', top: '8%' },
  { left: '12%', top: '52%' },
  { left: '56%', top: '62%' },
];

export interface MapCompactPreviewProps {
  dataElScope: DataElScope;
  entries: MapLocationEntry[];
  /** Whether a coordinate pin's chip trails with its own lat/lon. The same
   *  embedder opt-in the expanded rail honours, so collapsing a block cannot
   *  bring back a restatement its host already carries. */
  showCoordinateDetail: boolean;
}

export function MapCompactPreview({
  dataElScope,
  entries,
  showCoordinateDetail,
}: MapCompactPreviewProps): ReactElement | null {
  if (entries.length === 0) {
    return null;
  }

  const places = entries.slice(0, COMPACT_PLACE_LIMIT);
  const hiddenCount = Math.max(0, entries.length - places.length);

  return (
      <div
        data-el={dataEl(dataElScope, 'MapCompactPreview/Root')}
        className="map-compact-preview"
        style={COMPACT_PREVIEW_ROOT_STYLE}
      >
        <div
          className="map-compact-preview__stage map-compact-preview__stage--grid map-compact-preview__stage--edge-feather"
        >
          <div className="map-compact-preview__grid" aria-hidden />
          <span
            className="map-compact-preview__orb map-compact-preview__orb--pocket"
            aria-hidden
          >
            <span className="map-compact-preview__orb-spin">
              <span className="map-compact-preview__orb-meridian" />
              <span className="map-compact-preview__orb-meridian map-compact-preview__orb-meridian--late" />
              <span className="map-compact-preview__orb-equator" />
            </span>
          </span>
          {places.map((entry, index) => {
            const seat = GRID_SEATS[index] ?? GRID_SEATS[0];
            const row = mapLocationRowText(entry);
            const detailText =
              showCoordinateDetail || !isCoordinateDetailRow(entry) ? row.detail : null;
            return (
              <div
                key={entry.key}
                className="map-compact-preview__place-chip"
                style={{ left: seat.left, top: seat.top, ...PLACE_LABEL_STYLE }}
              >
                <span className="map-compact-preview__place-chip-index">{index + 1}</span>
                <span className="map-compact-preview__place-chip-name" dir={AUTO_TEXT_DIRECTION}>
                  {row.primary}
                </span>
                {detailText ? (
                  <span className="map-compact-preview__place-chip-detail" dir={AUTO_TEXT_DIRECTION}>
                    {detailText}
                  </span>
                ) : null}
              </div>
            );
          })}
          {renderOverflowChip({ hiddenCount })}
        </div>
      </div>
  );
}

function renderOverflowChip({
  hiddenCount,
}: {
  hiddenCount: number;
}): ReactElement | null {
  if (hiddenCount <= 0) {
    return null;
  }

  return (
    <div
      className="map-compact-preview__overflow"
      style={PLACE_LABEL_STYLE}
    >
      {`+${hiddenCount} more`}
    </div>
  );
}
